import { LogPhotoStrip, logPhotoUrls } from "../LogPhotoStrip";
import CoachStrengthLogView from "./CoachStrengthLogView";
import { isStrengthLikeWorkout } from "../../lib/coaching";
import { PencilLine } from "lucide-react";
import React, { useEffect, useState } from "react";
import { X, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import {
  coachOverrideWorkoutLog,
  coachDeleteWorkoutLog,
  coachListWorkoutComments,
  coachPostWorkoutComment,
} from "../../lib/api";
import { parseDurationToMinutes, formatMinutesAsHhMm } from "../../lib/duration";
import WorkoutCommentsThread from "../WorkoutCommentsThread";

/**
 * Coach-side modal to edit (or create) an athlete's workout log entry.
 *
 * Common usage: the athlete logged a partial 11 km on a prescribed 17 km
 * and finished the rest later — the coach bumps the entry to complete and
 * corrects mileage + time without bothering the athlete.
 *
 * Props:
 *   athleteId, programId, weekNumber, dayLabel, workoutId
 *   existingLog: optional current row (null = creating one)
 *   workout: the prescribed workout (used for placeholder defaults)
 *   onClose(updatedLogOrNull)
 */
export default function CoachWorkoutLogOverrideModal({
  athleteId,
  programId,
  weekNumber,
  dayLabel,
  workoutId,
  existingLog,
  workout,
  onClose,
  onEditWorkout,
}) {
  const [status, setStatus] = useState(existingLog?.status || "complete");
  const [distance, setDistance] = useState(
    existingLog?.distance_actual_km != null ? String(existingLog.distance_actual_km) : "",
  );
  const [duration, setDuration] = useState(
    existingLog?.duration_actual_min != null ? String(existingLog.duration_actual_min) : "",
  );
  const [athleteNote, setAthleteNote] = useState(existingLog?.athlete_note || "");
  const [coachNote, setCoachNote] = useState(existingLog?.coach_note || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ESC closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const prescribed = workout?.duration_or_distance || "";

  const onSave = async () => {
    setSaving(true);
    try {
      const result = await coachOverrideWorkoutLog(athleteId, {
        program_id: programId,
        week_number: weekNumber,
        day_label: dayLabel,
        workout_id: workoutId,
        status,
        distance_actual_km: distance === "" ? null : Number(distance),
        duration_actual_min: (() => {
          if (duration === "") return null;
          const parsed = parseDurationToMinutes(duration);
          return parsed == null ? null : parsed;
        })(),
        athlete_note: athleteNote,
        coach_note: coachNote,
      });
      toast.success("Saved (coach override)");
      onClose(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!existingLog) {
      onClose(null);
      return;
    }
    if (!window.confirm("Clear this log entry? The athlete's actuals for this workout will disappear from the report.")) {
      return;
    }
    setDeleting(true);
    try {
      await coachDeleteWorkoutLog(athleteId, programId, weekNumber, dayLabel, workoutId);
      toast.success("Log cleared");
      onClose("deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={() => onClose(null)}
      data-testid="coach-log-override-modal"
    >
      <div
        className="w-full sm:max-w-lg bg-white border border-[var(--tir-ink)] max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--tir-border)]">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold">
              Coach override
            </div>
            <h2 className="font-serif-display text-2xl leading-tight mt-1">
              {workout?.title || "Athlete log"}
            </h2>
            {onEditWorkout && (
              <button
                type="button"
                onClick={() => onEditWorkout()}
                data-testid="coach-log-edit-workout"
                className="mt-1.5 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)]"
                title="Open the workout plan editor (intervals, zones, exercises)"
              >
                <PencilLine size={11} /> Edit workout plan
              </button>
            )}
            <div className="text-xs text-[var(--tir-muted)] mt-1">
              {dayLabel} · Week {weekNumber}
              {prescribed && (
                <span>
                  {" "}· prescribed <span className="font-mono-tir text-[var(--tir-ink)]">{prescribed}</span>
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose(null)}
            className="p-1 text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
            data-testid="coach-log-override-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-2">
              Status
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "complete", label: "Complete", colour: "#2F855A" },
                { v: "partial", label: "Partial", colour: "#9C7A1B" },
                { v: "skipped", label: "Skipped", colour: "#7A1F2B" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setStatus(o.v)}
                  data-testid={`coach-log-status-${o.v}`}
                  className={`px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold border transition-colors ${
                    status === o.v
                      ? "text-white"
                      : "bg-white text-[var(--tir-ink)] border-[var(--tir-border)] hover:border-[var(--tir-ink)]"
                  }`}
                  style={
                    status === o.v
                      ? { background: o.colour, borderColor: o.colour }
                      : undefined
                  }
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {!isStrengthLikeWorkout(workout) && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                Distance (km)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                placeholder="0"
                data-testid="coach-log-distance"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-base font-mono-tir focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                Duration
              </label>
              <input
                type="text"
                inputMode="text"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="e.g. 45m, 1:30, 1h30, 1.5h"
                data-testid="coach-log-duration"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-base font-mono-tir focus:outline-none"
              />
              {duration && parseDurationToMinutes(duration) != null && (
                <div className="mt-1 text-[10px] text-[var(--tir-muted)] italic" data-testid="coach-log-duration-parsed">
                  = {formatMinutesAsHhMm(parseDurationToMinutes(duration))} ({Math.round(parseDurationToMinutes(duration))} min)
                </div>
              )}
            </div>
          </div>
          )}

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Athlete's note
              {existingLog?.athlete_note && (
                <span className="ml-2 text-[9px] normal-case tracking-normal text-[var(--tir-muted)]/70">
                  (originally written by the athlete — edit at your discretion)
                </span>
              )}
            </label>
            <textarea
              value={athleteNote}
              onChange={(e) => setAthleteNote(e.target.value)}
              rows={3}
              placeholder="Athlete-side notes about how the workout went…"
              data-testid="coach-log-athlete-note"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none leading-relaxed"
            />
          </div>

          {workout?.strength_blocks?.length > 0 && (
            <div className="mb-4" data-testid="coach-log-strength-sets">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1.5">What they lifted</div>
              <CoachStrengthLogView
                athleteId={athleteId}
                programId={programId}
                workoutId={workoutId}
                blocks={workout.strength_blocks}
              />
            </div>
          )}
          {logPhotoUrls(existingLog).length > 0 && (
            <div className="mb-4" data-testid="coach-log-photos-section">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1.5">Athlete&apos;s photos</div>
              <LogPhotoStrip log={existingLog} size="md" max={10} testid="coach-log-photos" />
            </div>
          )}
          {existingLog?.nutrition && (
            <div
              className="border border-[var(--tir-border)] bg-[var(--tir-ivory)]/60 rounded-sm px-3 py-2"
              data-testid="coach-log-nutrition"
            >
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-0.5">
                Intra-workout nutrition
              </div>
              <div className="text-[12px] text-[var(--tir-ink)] leading-snug">
                {[
                  existingLog.nutrition.items,
                  existingLog.nutrition.calories_per_hour != null
                    ? `${existingLog.nutrition.calories_per_hour} cal/hr`
                    : null,
                  existingLog.nutrition.carbs_per_hour != null
                    ? `${existingLog.nutrition.carbs_per_hour} g carbs/hr`
                    : null,
                  existingLog.nutrition.fluids_ml != null
                    ? `${existingLog.nutrition.fluids_ml} ml fluids`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          )}
          {existingLog?.gear && (
            <div
              className="border border-[var(--tir-border)] bg-[var(--tir-ivory)]/60 rounded-sm px-3 py-2"
              data-testid="coach-log-gear"
            >
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-0.5">
                Conditions &amp; gear
              </div>
              <div className="text-[12px] text-[var(--tir-ink)] leading-snug">
                {[
                  existingLog.gear.temperature,
                  existingLog.gear.shoes ? `Shoes: ${existingLog.gear.shoes}` : null,
                  existingLog.gear.layers ? `Layers: ${existingLog.gear.layers}` : null,
                ].filter(Boolean).join(" · ")}
                {existingLog.gear.note && <span className="italic text-[var(--tir-muted)]"> — “{existingLog.gear.note}”</span>}
              </div>
            </div>
          )}

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Coach's private note
              <span className="ml-2 text-[9px] normal-case tracking-normal text-[var(--tir-muted)]/70">
                (only visible to you)
              </span>
            </label>
            <textarea
              value={coachNote}
              onChange={(e) => setCoachNote(e.target.value)}
              rows={2}
              placeholder="e.g. Finished the remaining 6 km in the evening — bumped to complete."
              data-testid="coach-log-coach-note"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none leading-relaxed"
            />
          </div>

          {/* Threaded conversation — coach can reply directly to the
              athlete's note. They get an email + an in-app red-dot until
              they read it. */}
          <WorkoutCommentsThread
            side="coach"
            athleteId={athleteId}
            programId={programId}
            weekNumber={weekNumber}
            dayLabel={dayLabel}
            workoutId={workoutId}
            listFn={coachListWorkoutComments}
            postFn={coachPostWorkoutComment}
          />
        </div>

        <div className="px-5 py-3 border-t border-[var(--tir-border)] flex flex-wrap items-center gap-2 bg-[var(--tir-cream)]/30">
          {existingLog && (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting || saving}
              data-testid="coach-log-delete"
              className="inline-flex items-center gap-1 px-3 py-2 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] border border-[var(--tir-burgundy)]/40 hover:bg-[var(--tir-burgundy)] hover:text-white transition-colors"
            >
              <Trash2 size={12} /> {deleting ? "Clearing…" : "Clear log"}
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => onClose(null)}
              className="px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] border border-[var(--tir-border)] hover:border-[var(--tir-ink)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || deleting}
              data-testid="coach-log-save"
              className="inline-flex items-center gap-2 px-5 py-2 bg-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy-dark)] disabled:opacity-50 text-white text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors"
            >
              <Save size={12} /> {saving ? "Saving…" : "Save override"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
